import { createDaiStreamManagerBuilder, createPlayerBuilder as createImaPlayerBuilder, DaiStreamMetadata } from 'fl-ads-ima';
import { ConsumptionType, ContentAuthorizationData, PlatformAsset, PlaybackMode } from 'fl-content-authorization';
import { createLogger, LoggerLevel } from 'fl-foundation';
import { PlatformAuthorizer } from 'fl-platform-authorizer';
import { getLicenseHandler } from 'fl-platform-player';
import { createComposablePlayer, createPlayerBuilder, PlayerBuilder } from 'fl-player';
import {
  ComposablePlayer,
  DrmScheme,
  MediaType,
  PlaybackAutoSeekMode,
  PlaybackFastForwardMode,
  PlaybackInterruptedMode,
  PlaybackOptions,
  PlaybackRepeatedMode,
  PlaybackRewindMode,
  Player,
  RequestType,
  TrackInfo,
  TrackType,
} from 'fl-player-interface';
import { ResourceVm } from 'qp-ui-interface';
import { DeviceTypeModel, getDeviceType } from 'qp-utility';
import { environment } from '../../../environments/environment';
import { GetPlatformPlayerModel } from '../interfaces/player.interface';
import { LocalStorageKeys } from '../LocalStorageKeys';
import { CatchupInfo } from '../models/epgData';
import { AppConfig } from '../services/config/config.service';
import { LocalStorageService } from '../services/local-storage/local-storage.service';
import { getIFAPlatform } from './deviceUtils';
import { isLoggedIn } from './LoginUtils';
import { getPurposeConsentStatus } from './oneTrustUtils';

declare const window: Window &
  typeof globalThis & {
    env: any;
    deviceModel: any;
    showDotAnimation: any;
    xSupportedResolution: any;
    xSupportedAudio: any;
    doNotTrack: any;
  };

declare let navigator: any;
declare let google: any;

export const getQualityPreference = (localStorage: LocalStorageService) => {
  return localStorage.getItem(LocalStorageKeys.QUALITY_PREFERENCE) ? localStorage.getItem(LocalStorageKeys.QUALITY_PREFERENCE) : 'Auto';
};

export const getSubtitlesPreference = (localStorage: LocalStorageService) => {
  return localStorage.getItem(LocalStorageKeys.SUBTITLES_LANGUAGE) || 'off';
};

const getCatalogType = (metadataType: string): string => {
  switch (metadataType) {
    case 'webseries':
    case 'webepisode':
      return 'webepisode';
    default:
      return metadataType;
  }
};

export const getConsumptionType = (metadataType: string): ConsumptionType => {
  switch (metadataType) {
    case 'channel':
    case 'livechannel':
    case 'liveevent':
      return ConsumptionType.LIVE;
    default:
      return ConsumptionType.VOD;
  }
};

const getPlaybackMode = (metadataType: string): PlaybackMode | undefined => {
  switch (metadataType) {
    case 'channel':
    case 'livechannel':
    case 'liveevent':
      return PlaybackMode.LIVE;
    default:
      return undefined;
  }
};

export const getHeaders = (shortLivedToken?: string): any => {
  const X_SUPPORTED_RESOLUTION = 'X-supported-resolution';
  const X_SUPPORTED_AUDIO = 'X-supported-audio';
  const X_ADOBE_AUTH_HEADER = 'X-Adobe-Authorization';

  const headers = {
    [X_SUPPORTED_RESOLUTION]: window.xSupportedResolution,
    [X_SUPPORTED_AUDIO]: window.xSupportedAudio?.join(','),
  };
  if (shortLivedToken) {
    headers[X_ADOBE_AUTH_HEADER] = shortLivedToken;
  }
  return headers;
};

export const getPlatformAsset = (contentId: string, metadataType: string, catchupInfo?: CatchupInfo): PlatformAsset => {
  let model = '';
  if (navigator.userAgent.includes('Tizen')) {
    const arr = navigator.userAgent.split(' ');
    model = arr[1] + ' ' + arr[2] + ' ' + arr[3] + ' ' + arr[4] + ' ';
  } else if (navigator.userAgent.includes('Web0S')) {
    const arr = navigator.userAgent.split(' ');
    model = arr[1] + ' ' + arr[2];
  }
  const platformAsset: PlatformAsset = {
    mediaID: contentId,
    consumptionType: getConsumptionType(metadataType),
    catalogType: getCatalogType(metadataType),
    mediaType: getMediaType(),
    drmScheme: getDrmScheme(),
    deviceManufacturer: 'web', //TODO: use comcast getDeviceManufacturer(),
    deviceOs: navigator.userAgent,
    deviceModelName: window.deviceModel,
    deviceModelNumber: model.trim(),
    supportedResolution: window.xSupportedResolution,
    supportedAudio: window.xSupportedAudio?.join(','),
  };

  const playbackMode = getPlaybackMode(metadataType);
  if (playbackMode) {
    platformAsset.playbackMode = playbackMode;
  }

  if (catchupInfo) {
    platformAsset.playbackMode = PlaybackMode.CATCHUP;
    platformAsset.startTime = catchupInfo.startTime;
    platformAsset.endTime = catchupInfo.endTime;
  }

  return platformAsset;
};

// TODO: replace isComcast() by qp-ui-lib utility function validation
export const getDrmScheme = (): DrmScheme => {
  return DrmScheme.WIDEVINE;
  // return getBrowserDetector().isSafari() ? DrmScheme.FAIRPLAY : DrmScheme.WIDEVINE;
};

// TODO: replace isComcast() by qp-ui-lib utility function validation
export const getMediaType = (): MediaType => {
  // return getBrowserDetector().isSafari() ? MediaType.HLS : MediaType.DASH;
  return MediaType.DASH;
};

const getBufferConfig = (applicationConfig: AppConfig) => {
  const playbackBufferOptions: Pick<PlaybackOptions, 'minBuffer' | 'maxBuffer' | 'bufferBehind' | 'ignoreMinBufferTime'> = {};

  if (applicationConfig.minBuffer) {
    playbackBufferOptions.minBuffer = parseFloat(applicationConfig.minBuffer);
  }

  if (applicationConfig.maxBuffer) {
    playbackBufferOptions.maxBuffer = parseFloat(applicationConfig.maxBuffer);
  }

  if (applicationConfig.bufferBehind) {
    playbackBufferOptions.bufferBehind = parseFloat(applicationConfig.bufferBehind);
  }

  if (applicationConfig.ignoreMinBufferTime && typeof applicationConfig.ignoreSuggestedPresentationDelay === 'boolean') {
    playbackBufferOptions.ignoreMinBufferTime = applicationConfig.ignoreMinBufferTime;
  }

  return playbackBufferOptions;
};

const getNetworkConfig = (applicationConfig: AppConfig) => {
  const playbackNetworkConfig: Pick<PlaybackOptions, 'networkRetries' | 'networkTimeout' | 'connectionTimeout' | 'useNetworkInformation'> =
    {};

  if (applicationConfig.networkRetries) {
    playbackNetworkConfig.networkRetries = parseFloat(applicationConfig.networkRetries);
  }

  if (applicationConfig.networkTimeout) {
    playbackNetworkConfig.networkTimeout = parseFloat(applicationConfig.networkTimeout);
  }

  if (applicationConfig.connectionTimeout) {
    playbackNetworkConfig.connectionTimeout = parseFloat(applicationConfig.connectionTimeout);
  }

  if (typeof applicationConfig.useNetworkInformation === 'boolean') {
    playbackNetworkConfig.useNetworkInformation = applicationConfig.useNetworkInformation;
  }

  return playbackNetworkConfig;
};

const getVodPlaybackOptions = (applicationConfig: AppConfig): PlaybackOptions => {
  const playbackOptions: PlaybackOptions = {
    autoPlayOnLoad: true,
    shouldRetryOnErrors: true,
    maxRetries: 1,
    retiresAttempted: 0,
    repeatWhenFinished: false,
    enforceHDCPCompliance: false,
    initialPlaybackTime: undefined,
    ...getBufferConfig(applicationConfig),
    ...getNetworkConfig(applicationConfig),
  };

  if (applicationConfig.stallTimeout) {
    playbackOptions.stallTimeout = parseFloat(applicationConfig.stallTimeout);
  }

  if (applicationConfig.connectionTimeout) {
    playbackOptions.connectionTimeout = parseFloat(applicationConfig.connectionTimeout);
  }

  if (applicationConfig.retryDelay) {
    playbackOptions.retryDelay = parseFloat(applicationConfig.retryDelay);
  }

  if (applicationConfig.preferredBitrate) {
    playbackOptions.preferredBitrate = parseFloat(applicationConfig.preferredBitrate);
  }

  if (applicationConfig.inaccurateManifestTolerance) {
    playbackOptions.inaccurateManifestTolerance = parseFloat(applicationConfig.inaccurateManifestTolerance);
  }

  if (applicationConfig.ignoreSuggestedPresentationDelay && typeof applicationConfig.ignoreSuggestedPresentationDelay === 'boolean') {
    playbackOptions.ignoreSuggestedPresentationDelay = applicationConfig.ignoreSuggestedPresentationDelay;
  }

  if (applicationConfig.defaultPresentationDelay) {
    playbackOptions.defaultPresentationDelay = parseFloat(applicationConfig.defaultPresentationDelay);
  }

  if (applicationConfig.liveMinTotalBytes) {
    playbackOptions.minTotalBytes = parseFloat(applicationConfig.liveMinTotalBytes);
  }

  if (applicationConfig.minBytes) {
    playbackOptions.minBytes = parseFloat(applicationConfig.minBytes);
  }

  if (applicationConfig.fastHalfLife) {
    playbackOptions.fastHalfLife = parseFloat(applicationConfig.fastHalfLife);
  }

  if (applicationConfig.slowHalfLife) {
    playbackOptions.slowHalfLife = parseFloat(applicationConfig.slowHalfLife);
  }

  if (applicationConfig.bandwidthUpgradeTarget) {
    playbackOptions.bandwidthUpgradeTarget = parseFloat(applicationConfig.bandwidthUpgradeTarget);
  }

  if (applicationConfig.bandwidthDowngradeTarget) {
    playbackOptions.bandwidthDowngradeTarget = parseFloat(applicationConfig.bandwidthDowngradeTarget);
  }

  if (applicationConfig.switchInterval) {
    playbackOptions.switchInterval = parseFloat(applicationConfig.switchInterval);
  }

  return playbackOptions;
};

const getLiveBufferOptionsConfig = (applicationConfig: AppConfig) => {
  const playbackBufferOptions: Pick<PlaybackOptions, 'minBuffer' | 'maxBuffer' | 'bufferBehind' | 'ignoreMinBufferTime'> = {};

  if (applicationConfig.liveMinBuffer) {
    playbackBufferOptions.minBuffer = parseFloat(applicationConfig.liveMinBuffer);
  }

  if (applicationConfig.liveMaxBuffer) {
    playbackBufferOptions.maxBuffer = parseFloat(applicationConfig.liveMaxBuffer);
  }

  if (applicationConfig.liveBufferBehind) {
    playbackBufferOptions.bufferBehind = parseFloat(applicationConfig.liveBufferBehind);
  }

  if (applicationConfig.liveIgnoreMinBufferTime && typeof applicationConfig.ignoreSuggestedPresentationDelay === 'boolean') {
    playbackBufferOptions.ignoreMinBufferTime = applicationConfig.liveIgnoreMinBufferTime;
  }
  return playbackBufferOptions;
};

const getLiveNetworkConfig = (applicationConfig: AppConfig) => {
  const playbackNetworkConfig: Pick<PlaybackOptions, 'networkRetries' | 'networkTimeout' | 'connectionTimeout' | 'useNetworkInformation'> =
    {};

  if (applicationConfig.liveNetworkRetries) {
    playbackNetworkConfig.networkRetries = parseFloat(applicationConfig.liveNetworkRetries);
  }

  if (applicationConfig.liveNetworkTimeout) {
    playbackNetworkConfig.networkTimeout = parseFloat(applicationConfig.liveNetworkTimeout);
  }

  if (applicationConfig.liveConnectionTimeout) {
    playbackNetworkConfig.connectionTimeout = parseFloat(applicationConfig.liveConnectionTimeout);
  }

  if (typeof applicationConfig.liveUseNetworkInformation === 'boolean') {
    playbackNetworkConfig.useNetworkInformation = applicationConfig.liveUseNetworkInformation;
  }
  return playbackNetworkConfig;
};

const getLivePlaybackOptions = (applicationConfig: AppConfig): PlaybackOptions => {
  const playbackOptions: PlaybackOptions = {
    autoPlayOnLoad: true,
    shouldRetryOnErrors: true,
    maxRetries: 1,
    retiresAttempted: 0,
    repeatWhenFinished: false,
    enforceHDCPCompliance: false,
    initialPlaybackTime: undefined,
    ...getLiveBufferOptionsConfig(applicationConfig),
    ...getLiveNetworkConfig(applicationConfig),
  };

  if (applicationConfig.liveStallTimeout) {
    playbackOptions.stallTimeout = parseFloat(applicationConfig.liveStallTimeout);
  }

  if (applicationConfig.liveRetryDelay) {
    playbackOptions.retryDelay = parseFloat(applicationConfig.liveRetryDelay);
  }

  if (applicationConfig.livePreferredBitrate) {
    playbackOptions.preferredBitrate = parseFloat(applicationConfig.livePreferredBitrate);
  }

  if (applicationConfig.liveInaccurateManifestTolerance) {
    playbackOptions.inaccurateManifestTolerance = parseFloat(applicationConfig.liveInaccurateManifestTolerance);
  }

  if (applicationConfig.liveIgnoreSuggestedPresentationDelay && typeof applicationConfig.ignoreSuggestedPresentationDelay === 'boolean') {
    playbackOptions.ignoreSuggestedPresentationDelay = applicationConfig.liveIgnoreSuggestedPresentationDelay;
  }

  if (applicationConfig.liveDefaultPresentationDelay) {
    playbackOptions.defaultPresentationDelay = parseFloat(applicationConfig.liveDefaultPresentationDelay);
  }

  if (applicationConfig.liveMinTotalBytes) {
    playbackOptions.minTotalBytes = parseFloat(applicationConfig.liveMinTotalBytes);
  }

  if (applicationConfig.liveMinBytes) {
    playbackOptions.minBytes = parseFloat(applicationConfig.liveMinBytes);
  }

  if (applicationConfig.liveFastHalfLife) {
    playbackOptions.fastHalfLife = parseFloat(applicationConfig.liveFastHalfLife);
  }

  if (applicationConfig.liveSlowHalfLife) {
    playbackOptions.slowHalfLife = parseFloat(applicationConfig.liveSlowHalfLife);
  }

  if (applicationConfig.liveBandwidthUpgradeTarget) {
    playbackOptions.bandwidthUpgradeTarget = parseFloat(applicationConfig.liveBandwidthUpgradeTarget);
  }

  if (applicationConfig.liveBandwidthDowngradeTarget) {
    playbackOptions.bandwidthDowngradeTarget = parseFloat(applicationConfig.liveBandwidthDowngradeTarget);
  }

  if (applicationConfig.liveSwitchInterval) {
    playbackOptions.switchInterval = parseFloat(applicationConfig.liveSwitchInterval);
  }
  return playbackOptions;
};

export const getPlaybackOptions = (applicationConfig: AppConfig, initialPlaybackTime = 0, isLive = false): PlaybackOptions => {
  const navigatorAgent = navigator.userAgent;
  const chrome = navigatorAgent.substr(navigatorAgent.indexOf('Chrome/'), navigatorAgent.indexOf('.')).split('/');
  let playbackOptions;
  if (!isLive) {
    playbackOptions = getVodPlaybackOptions(applicationConfig);
  } else if (isLive) {
    playbackOptions = getLivePlaybackOptions(applicationConfig);
    return playbackOptions;
  }
  playbackOptions.initialPlaybackTime = initialPlaybackTime;

  if (parseInt(chrome[1], 10) < 40 || (!navigatorAgent.includes('Web0S') && !navigatorAgent.includes('Tizen'))) {
    // For stop buffering
    playbackOptions.minBuffer = 2;
    playbackOptions.maxBuffer = 3;
    playbackOptions.bufferBehind = 2;

    return playbackOptions;
  }

  return playbackOptions;
};

const extractTracksByType = (trackList: TrackInfo[], activeTracks: TrackInfo[], type: TrackType): any[] => {
  if (!trackList) return [];
  const filteredNoUndefined = trackList.filter((track) => track.type === type);

  if (filteredNoUndefined.length === 0) return [];
  const t = filteredNoUndefined[0];
  return [
    {
      type: t.type,
      language: t.language,
      name: 'On',
      active:
        activeTracks.filter((a) => a.type === type).length > 0
          ? activeTracks.filter((a) => a.type === type)[0].language === t.language
          : false,
    },
  ];
};

export const getTextTrackList = (playerInstance: Player): any[] => {
  if (!playerInstance) return [];
  const trackList = playerInstance.getAllTracks();
  const activeTracks = playerInstance.getActiveTracks();
  const textTrackList = extractTracksByType(trackList, activeTracks, TrackType.TEXT);
  const offTextTrack = {
    type: TrackType.TEXT,
    language: 'off',
    name: 'Off',
    active: activeTracks.length > 0,
  };
  textTrackList.unshift(offTextTrack);
  return textTrackList;
};

export const getAudioTrackList = (playerInstance: Player): any[] => {
  if (!playerInstance) return [];
  const trackList = playerInstance.getAllTracks();
  const activeTracks = playerInstance.getActiveTracks();
  return extractTracksByType(trackList, activeTracks, TrackType.AUDIO);
};

export const getComposablePlayer = (playerInstance: Player): ComposablePlayer => {
  return createComposablePlayer(playerInstance);
};

export const getThumbnailData = (
  resourceId: string,
  contentAuthorizationData: ContentAuthorizationData,
  showThumbnails: boolean,
  appConfig: AppConfig,
  appVersion: string
): { thumbnailConfig: any; thumbnailUrl: string } => {
  if (!showThumbnails) return { thumbnailUrl: '', thumbnailConfig: undefined };
  return {
    thumbnailUrl: `${appConfig.imageResizeURL}/image/${resourceId}/${contentAuthorizationData?.keyframe?.name}?width=${
      contentAuthorizationData?.keyframe?.width * contentAuthorizationData?.keyframe?.columns
    }&height=${
      contentAuthorizationData?.keyframe?.height * contentAuthorizationData?.keyframe?.rows
    }&dt=${getDeviceType()}&appVersion=${appVersion}`,
    thumbnailConfig: {
      numRows: contentAuthorizationData?.keyframe?.rows,
      numColumns: contentAuthorizationData?.keyframe?.columns,
      thumbnailWidth: contentAuthorizationData?.keyframe?.width,
      thumbnailHeight: contentAuthorizationData?.keyframe?.height,
      keyframeDuration: contentAuthorizationData?.keyframe?.frequency,
      totalSpriteIndices: contentAuthorizationData?.keyframe?.ia?.length,
    },
  };
};

const buildPlayer = (
  playbackOptions: PlaybackOptions,
  contentAuthorizationData: ContentAuthorizationData,
  video: HTMLVideoElement,
  certificate: ArrayBuffer,
  showThumbnails: boolean,
  thumbnailsUrl: string,
  thumbnailConfig: any
): Player => {
  try {
    const playerBuilder: PlayerBuilder = createPlayerBuilder()
      .mediaElement(video)
      .certificate(certificate)
      .mediaUrl(contentAuthorizationData.contentUrl)
      .playbackOptions(playbackOptions)
      .drmLicenseUrl(contentAuthorizationData.drmLicenseUrl)
      .drmScheme(contentAuthorizationData.drmScheme)
      .mediaType(contentAuthorizationData.mediaType);

    if (showThumbnails && thumbnailsUrl && thumbnailsUrl !== '') {
      playerBuilder.enableThumbnailPreview(thumbnailsUrl, thumbnailConfig).webVTTThumbnailPreview(thumbnailsUrl);
    }
    return playerBuilder.build();
  } catch {
    return createPlayerBuilder()
      .mediaElement(video)
      .mediaUrl(contentAuthorizationData.contentUrl)
      .playbackOptions(playbackOptions)
      .drmLicenseUrl(contentAuthorizationData.drmLicenseUrl)
      .drmScheme(contentAuthorizationData.drmScheme)
      .mediaType(contentAuthorizationData.mediaType)
      .build();
  }
};

const getPlayer = (
  playbackOptions: PlaybackOptions,
  contentAuthorizationData: ContentAuthorizationData,
  video: HTMLVideoElement,
  certificate: ArrayBuffer,
  showThumbnails: boolean,
  thumbnailsUrl: string,
  thumbnailsConfig: any
): Promise<Player> => {
  try {
    return Promise.resolve(
      buildPlayer(playbackOptions, contentAuthorizationData, video, certificate, showThumbnails, thumbnailsUrl, thumbnailsConfig)
    );
  } catch (err) {
    console.log(err);
    return Promise.resolve(buildPlayer(playbackOptions, contentAuthorizationData, video, undefined, false, '', undefined));
  }
};

export const getPlatformPlayer = (getPlatformPlayerModel: GetPlatformPlayerModel): Promise<Player> => {
  const { seekPos, applicationConfig, isLive, contentAuthorizationData, video, showThumbnails, resourceId, certificate } =
    getPlatformPlayerModel;
  const playbackOptions: PlaybackOptions = getPlaybackOptions(applicationConfig, seekPos, isLive);
  if (getDeviceType() === DeviceTypeModel.VIZIO) {
    if (applicationConfig.vizioPlaybackOptimization) {
      playbackOptions.safariBehaviourForLiveHls = applicationConfig.safariBehaviourForLiveHlsVizio;
      playbackOptions.bufferTimeoutMs = applicationConfig.bufferTimeoutMsVizio;
    }
    playbackOptions.forceTransmuxTS = true;
  }
  try {
    const navigatorAgent = navigator.userAgent;
    const chromeVersion = parseInt(navigatorAgent.match(/Chrome\/(\d+)/)?.[1] || '0', 10);
    const { thumbnailUrl, thumbnailConfig } = getThumbnailData(
      resourceId,
      contentAuthorizationData,
      showThumbnails,
      applicationConfig,
      '1'
    );

    if (chromeVersion < 40 || (!navigatorAgent.includes('Web0S') && !navigatorAgent.includes('Tizen'))) {
      return getPlayer(playbackOptions, contentAuthorizationData, video, certificate, showThumbnails, thumbnailUrl, thumbnailConfig);
    }
    return getPlayer(playbackOptions, contentAuthorizationData, video, certificate, showThumbnails, thumbnailUrl, thumbnailConfig);
  } catch (e) {
    return getPlayer(playbackOptions, contentAuthorizationData, video, undefined, false, '', undefined);
  }
};

/**
 * Get the default license handler from FLPlatformPlayer
 * and set those as request/response interceptors on the player instance.
 *
 * The license handler injects the authorization tokens required for
 * DRM proxy server.
 */
export const handleLicenseRequest = async (platformAuthorizer: PlatformAuthorizer, playerInstance: Player): Promise<Player> => {
  try {
    const licenseHandler = await getLicenseHandler(platformAuthorizer);
    const requestInterceptor = async (type: RequestType, request: any) => {
      if (type === RequestType.LICENSE) licenseHandler.licenseRequestHandler(request);
    };
    playerInstance.setRequestInterceptor(requestInterceptor);
  } finally {
    return playerInstance;
  }
};

/**
 * Validate if thumbnails are supported
 * based on the userAgent versions
 *
 * @returns true or false
 */
// export const thumbnailsPlatformValidation = (): boolean => {
//   const userAgent = navigator.userAgent;
//   if (userAgent.toLocaleLowerCase().includes('tizen')) {
//     const tizenVersion = navigator.userAgent.slice(30, 39);
//     const tizenNum = tizenVersion.split(' ')[1];
//     return parseFloat(tizenNum) > 5;
//   } else if (userAgent.toLocaleLowerCase().includes('web0s')) {
//     const chrome = userAgent.substr(userAgent.indexOf('Chrome/'), userAgent.indexOf('.')).split('/');
//     return parseInt(chrome[1], 10) >= 53;
//   } else if (userAgent.toLocaleLowerCase().includes('vizio')) {
//     const chrome = userAgent.substr(userAgent.indexOf('Chrome/'), userAgent.indexOf('.')).split('/');
//     return parseInt(chrome[1], 10) >= 8;
//   } else if (window.env === '%APP_ENV%') return true;
//   return false;
// };

/**
 * Check if playback is enabled by route data
 *
 * @param event
 * @returns
 */
export const isPlaybackEnabledByRoute = (currentUrl: string, tabsUrl: string[]): boolean => {
  const playbackUrls = ['/epg', '/player/', '/collection/'];
  return tabsUrl.some((url) => currentUrl.includes(url)) || playbackUrls.some((url) => currentUrl.includes(url)) || currentUrl === '/';
};

/**
 * Build instance of Ad Player
 * NOTE:
 * fl-ads-ima.createPlayerBuilder.build creates an "AdComposablePlayer" = AdPlayer
 * since the result is a composable player (check  .contentPlayer(playerInstance) )
 * this adPlayer instance would take care of the player.content (MAIN or AD or PROMO)
 * then we need to attach the usual player events to this AdCcomposablePlayer (progressupdate ...)
 *
 * @param playerInstance builded by fl-player
 * @param videoElement HTMLVideoElement
 * @param videoContainer HTMLElement wrapper of videoElement
 * @returns AdPlayer
 */
export const getAdStreamMetadata = (
  videoElement: HTMLVideoElement,
  videoContainer: HTMLElement,
  mediaType: MediaType,
  isLive: boolean,
  adTagParameters: any,
  contentUrl: string,
  cmsId?: string,
  contentId?: string,
  assetKey?: string
): Promise<DaiStreamMetadata> => {
  const streamManager = isLive
    ? createDaiStreamManagerBuilder()
        .ima(google?.ima)
        .adTagParameters(adTagParameters)
        .contentVideoElement(videoElement) // videoElement on which ad is played
        .contentContainerElement(videoContainer) // videoContainer on which ad UI and settings are rendered
        //.localization('fr')
        .asset(assetKey)
        .fallbackUrl(contentUrl)
        .mediaType(mediaType)
        .logger(createLogger(LoggerLevel.OFF, 'streamManager'))
        .build()
    : createDaiStreamManagerBuilder()
        .ima(google?.ima)
        .adTagParameters(adTagParameters)
        .contentVideoElement(videoElement) // videoElement on which ad is played
        .contentContainerElement(videoContainer) // videoContainer on which ad UI and settings are rendered
        //.localization('fr')
        .contentSource(cmsId)
        .fallbackUrl(contentUrl)
        .video(contentId)
        .mediaType(mediaType)
        .logger(createLogger(LoggerLevel.OFF, 'streamManager'))
        .build();

  return streamManager.getDaiMetadata();
};

/**
 * Create IMA player
 *
 * @param metadata
 * @param playerInstance
 * @param videoElement
 * @returns
 */
export const createIMADAIPlayer = (metadata: DaiStreamMetadata, playerInstance: Player, videoElement: HTMLVideoElement) => {
  const adPlaybackPolicy = {
    fastForwardMode: PlaybackFastForwardMode.TRICK_FW_PLAY_LAST,
    rewindMode: PlaybackRewindMode.TRICK_RW_SKIP_ALL,
    autoSeekMode: PlaybackAutoSeekMode.AUTO_SEEK_SKIP_ALL,
    repeatMode: PlaybackRepeatedMode.REPEAT_PLAY_ONCE,
    interruptMode: PlaybackInterruptedMode.INTERRUPT_RESUME,
  };
  return createImaPlayerBuilder()
    .ima(google.ima)
    .imaDaiManager(metadata.streamManager)
    .contentPlayer(playerInstance)
    .contentVideoElement(videoElement)
    .playbackPolicy(adPlaybackPolicy)
    .logger(createLogger(LoggerLevel.OFF, '[AD]'))
    .build();
};

/**
 * Verify if google object is undefined
 *
 * @returns
 */
export const isGoogleIMAUndefined = () => {
  return typeof google === 'undefined' || typeof google.ima === 'undefined' || typeof google.ima.dai === 'undefined';
};

/**
 * Get cust params encoded values
 *
 * @param custParamsString
 * @param carouselId
 * @param carouselTitle
 * @param navigationSection
 * @param mvpd
 * @param videoId
 * @param videoTitle
 * @param playerWidth
 * @param playerHeight
 * @returns
 */
export const getCustParams = (
  custParamsString: string,
  resource: ResourceVm,
  navigationSection: string = 'NA',
  mvpd: string = 'NA',
  playerWidth: number,
  playerHeight: number
): string => {
  custParamsString = custParamsString.replace(
    '~carousel_id~',
    (resource.containerId ?? 'NA').normalize('NFD').replace(/[\u0300-\u036f]|[^a-zA-Z0-9]/g, '')
  );
  custParamsString = custParamsString.replace(
    '~carousel_title~',
    (resource.containerName ?? 'NA').normalize('NFD').replace(/[\u0300-\u036f]|[^a-zA-Z0-9]/g, '')
  );
  custParamsString = custParamsString.replace(
    '~navigation_section~',
    navigationSection.normalize('NFD').replace(/[\u0300-\u036f]|[^a-zA-Z0-9]/g, '')
  );
  custParamsString = custParamsString.replace('~mvpd~', mvpd);
  custParamsString = custParamsString.replace('~video_id~', resource.id);
  custParamsString = custParamsString.replace(
    '~video_title~',
    (resource.title ?? 'NA').normalize('NFD').replace(/[\u0300-\u036f]|[^a-zA-Z0-9]/g, '')
  );
  custParamsString = custParamsString.replace('~pw~', playerWidth.toString());
  custParamsString = custParamsString.replace('~ph~', playerHeight.toString());
  // if (seasonCount) {
  //   custParamsString += `&series_season_count=${seasonCount}`
  // }
  return encodeURIComponent(custParamsString);
};

/**
 * Get the device Ad tracking configuration attributes
 */
const getDeviceTags = (localStorage: LocalStorageService) => {
  const deviceTags: { [key: string]: any } = {};
  //rdid =mdevice identifier
  if (localStorage.getItem(LocalStorageKeys.DEVICEREF)) deviceTags.rdid = localStorage.getItem(LocalStorageKeys.DEVICEREF);
  // is_lat= is limited tracking enabled
  //idtype = the type of device identifier
  deviceTags.idtype = getIFAPlatform();
  // sid = which is a privacy-preserving advertising identifier
  deviceTags.sid = '0';
  return deviceTags;
};

/**
 * Read the concent tags values and fill the ad tag values
 *
 * @returns
 */
const getConsentTags = async () => {
  const ctags: { [key: string]: any } = {};
  //npa = to indicate that the ad request should be treated as non-personalized
  //npa = 0 (If user opts in at the device settings level and in one trust(ON))
  //npa = 1(if user opts out in device settings or in onetrust(OFF))
  ctags.npa = '0';
  //rdp= 0 (If user opts in at the device settings level and in one trust(ON))
  //rdp = 1(if user opts out in device settings or in onetrust(OFF))"""
  ctags.rdp = '0';
  //"IMA
  // Set Onetrust constent acceptance value
  // ltd = 0 (If user opts in at the device settings level and in one trust(ON))
  // ltd = 1(if user opts out in device settings or in onetrust(OFF))
  ctags.ltd = '0';
  // is_lat= is limited tracking enabled
  // is_lat = 0 (If user opts in at the device settings level and in one trust(ON))
  // is_lat = 1(if user opts out in device settings or in onetrust(OFF))
  ctags.is_lat = '0';

  try {
    //Get the advertising privacy and playback policy
    //C0004 = Targeting
    const targetActivated = await getPurposeConsentStatus('C0004');
    //TODO: add device settings condition
    // response.limitAdTracking === false && targetActivated.consent
    ctags.npa = targetActivated.consent === 1 ? '0' : '1';
    ctags.rdp = targetActivated.consent === 1 ? '0' : '1';
    ctags.ltd = targetActivated.consent === 1 ? '0' : '1';
    ctags.is_lat = targetActivated.consent === 1 ? '0' : '1';
  } catch (e) {
    console.warn('[AD TAGS]', e);
  }
  return ctags;
};

/**
 * Create an object with the ad tag params from authorization custom and app client info
 *
 * @param authCustomValues
 * @param localStorage
 * @param isLiveContent
 * @param isCatchUpContent
 * @param resourceVm
 * @param currentnavigationSection
 * @param mvpdHash
 * @param deviceID
 * @param userID
 * @param playerWidth
 * @param playerHeight
 * @returns
 */
export const getAdTags = async (
  authCustomValues: { [key: string]: any },
  localStorage: LocalStorageService,
  isLiveContent: boolean,
  isCatchUpContent: boolean,
  resourceVm: ResourceVm,
  currentnavigationSection: string,
  adobeInfo: {
    mvpdHash: string;
    userID: string;
  },
  deviceID: string,
  playerWidth: number,
  playerHeight: number
) => {
  const adTags: { [key: string]: any } = {};
  if (authCustomValues) {
    //an = The app ID and name
    if (authCustomValues?.an) adTags.an = 'Univision';
    // iu = ad unit value to be used in ad request
    if (authCustomValues?.iu) {
      adTags.userType = isLoggedIn(localStorage) ? 'subscriber' : 'free';
      adTags.streamType = isLiveContent ? (isCatchUpContent ? 'catchup' : 'live') : 'vod';
      adTags.platform = 'comcast';
      adTags.iu = authCustomValues.iu
        .replace('~usertype~', adTags.userType)
        .replace('~streamtype~', adTags.streamType)
        .replace('~platfom~', adTags.platform);
    }
    // description_url = describe the video playing on the page
    // The description_url value must be URL-encoded for web page video players and CTV/OTT devices
    if (authCustomValues?.description_url) {
      adTags.description_url = encodeURI(
        authCustomValues.description_url.replace('~cty~', resourceVm.contentType).replace('~nu_prd~', resourceVm.uniqueId)
      );
    }
    // scor = stream correlator parameter accepts a variable value which should be an integer generated for each video stream.
    adTags.scor = '87767';
    // correlator = shared by multiple requests coming from the same page view
    // SDK overwrites it with its own value
    // Ensure that you set this value to a truly random, positive, numerical value that is not being reused by multiple page views
    if (authCustomValues?.correlator) adTags.correlator = '89950';
    // vpa = Indicates whether the ad starts through autoplay or click.
    adTags.vpa = 'click';
    // vpos = value which indicates whether the ad request is being sent from pre-roll, mid-roll or post-roll.
    if (authCustomValues?.vpos) adTags.vpos = authCustomValues.vpos.replace('~vpos~', 'preroll');
    // msid = values which should be applied to requests sent from mobile app and connected TV devices.
    if (authCustomValues?.msid) adTags.msid = 'univision.com';
    // vpmute = indicates whether the ad playback starts while the video player is muted
    adTags.vpmute = '0';
    // live pod duration taken together, specify the duration range that a pod must match, in milliseconds.
    if (authCustomValues?.pmad) adTags.pmad = authCustomValues.pmad.replace('~pmad~', '-1');
    if (authCustomValues?.pmnd) adTags.pmnd = authCustomValues.pmnd.replace('~pmnd~', '0');
    if (authCustomValues?.pmxd) adTags.pmxd = authCustomValues.pmxd.replace('~pmxd~', '600000');
    // ppid = publisher provided id map to the user ID for both subscribers and anonymous users
    adTags.ppid = adobeInfo.userID ?? deviceID;
    // mvpd = orresponds to the classification of video asset.
    adTags.mvpd = adobeInfo.mvpdHash ?? 'NA';
    // app_bundle = app bundle name
    adTags.app_bundle = environment.app.bundle;
    // content info
    adTags.contentType = resourceVm.contentType;
    adTags.uniqueId = resourceVm.uniqueId;
    adTags.carouselId = resourceVm.containerId ?? 'NA';
    adTags.carouselTitle = resourceVm.containerName ?? 'NA';
    adTags.videoId = resourceVm.id;
    adTags.videoTitle = resourceVm.title;
    // video info
    adTags.pw = playerWidth?.toString();
    adTags.ph = playerHeight?.toString();
    // navigation current page
    adTags.navigationSection = currentnavigationSection;
    // url = full URL from which the request is sent
    if (authCustomValues?.url) adTags.url = encodeURI(authCustomValues.url);
    // user_agent = standard_user_agent
    if (authCustomValues?.user_agent) adTags.user_agent = navigator.userAgent;
    // video_root_id = standard_video_root_id
    // adTags.video_root_id = '';
    // custom params
    if (authCustomValues?.cust_params) {
      adTags.cust_params = getCustParams(
        authCustomValues.cust_params,
        resourceVm,
        currentnavigationSection,
        adobeInfo.mvpdHash,
        playerWidth,
        playerHeight
      );
    }
  }
  const ctags = await getConsentTags();
  return { ...adTags, ...ctags, ...getDeviceTags(localStorage) };
};
